import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Expand, X, Images } from 'lucide-react';
import styles from './WikiImageGallery.module.css';

export default function WikiImageGallery({ images }) {
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState({});
  const pointer = useRef(null);
  const swiped = useRef(false);
  const dialog = useRef(null);
  const close = useRef(null);
  const thumbs = useRef(null);
  const move = useCallback((direction) => setIndex((i) => (i + direction + images.length) % images.length), [images.length]);
  const current = images[index];
  useEffect(() => {
    const active = thumbs.current?.children[index];
    if (active && thumbs.current) {
      const left = active.offsetLeft - thumbs.current.offsetLeft;
      const right = left + active.offsetWidth;
      if (left < thumbs.current.scrollLeft) thumbs.current.scrollLeft = left;
      else if (right > thumbs.current.scrollLeft + thumbs.current.clientWidth) thumbs.current.scrollLeft = right - thumbs.current.clientWidth;
    }
  }, [index]);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    close.current?.focus();
    const keyboard = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); setOpen(false); }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1); }
      if (event.key === 'Tab') {
        const buttons = [...dialog.current.querySelectorAll('button,a[href]')];
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', keyboard);
    return () => { document.body.style.overflow = overflow; document.removeEventListener('keydown', keyboard); previous?.focus(); };
  }, [open, move]);
  const gesture = {
    onPointerDown: (e) => { swiped.current = false; pointer.current = { x: e.clientX, y: e.clientY }; },
    onPointerUp: (e) => {
      if (!pointer.current) return;
      const x = e.clientX - pointer.current.x, y = e.clientY - pointer.current.y;
      pointer.current = null;
      if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y) * 1.4) { swiped.current = true; move(x < 0 ? 1 : -1); }
    },
    onPointerCancel: () => { pointer.current = null; },
  };
  const arrows = <><button type="button" className={`${styles.arrow} ${styles.previous}`} onClick={() => move(-1)} aria-label="Предыдущее изображение"><ChevronLeft size={22} /></button><button type="button" className={`${styles.arrow} ${styles.next}`} onClick={() => move(1)} aria-label="Следующее изображение"><ChevronRight size={22} /></button></>;
  return <>
    <section className={styles.gallery} data-wiki-gallery aria-label="Изображения обновления" aria-roledescription="карусель" onKeyDown={(e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); move(e.key === 'ArrowLeft' ? -1 : 1); } }}>
      <div className={styles.header}><span><Images size={16} />В деталях</span><span role="status" aria-live="polite">{index + 1} / {images.length}</span></div>
      <div className={styles.stage} {...gesture}>
        <button type="button" className={styles.imageButton} aria-label={`Увеличить изображение ${index + 1}`} onClick={() => { if (!swiped.current) setOpen(true); }}>
          {failed[index] ? <span className={styles.unavailable}>Изображение не загрузилось. Открыть отдельно</span> : <img className={styles.slide} key={current.src} src={current.src} alt={current.alt || `Изображение обновления ${index + 1}`} draggable={false} onError={() => setFailed((old) => ({ ...old, [index]: true }))} />}
          <span className={styles.expand}><Expand size={15} />Развернуть</span>
        </button>
        {images.length > 1 && arrows}
      </div>
      {images.length > 1 && <div className={styles.thumbnails} ref={thumbs} aria-label="Выбор изображения">{images.map((image, i) => <button type="button" key={`${image.src}-${i}`} className={`${styles.thumb} ${index === i ? styles.active : ''}`} aria-label={`Показать изображение ${i + 1}`} aria-pressed={index === i} onClick={() => setIndex(i)}><img src={image.src} alt="" loading="lazy" draggable={false} /><span>{String(i + 1).padStart(2, '0')}</span></button>)}</div>}
    </section>
    {open && createPortal(<div className={styles.lightbox} role="dialog" aria-modal="true" aria-label="Просмотр изображений обновления" ref={dialog} onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <button type="button" ref={close} className={styles.close} onClick={() => setOpen(false)} aria-label="Закрыть просмотр"><X size={24} /></button>
      <div className={styles.fullImage} {...gesture}><img key={current.src} src={current.src} alt={current.alt || `Изображение обновления ${index + 1}`} draggable={false} /></div>
      {images.length > 1 && arrows}
      <div className={styles.fullFooter}><span role="status" aria-live="polite">{index + 1} / {images.length}</span><a href={current.src} target="_blank" rel="noopener noreferrer">Открыть оригинал <Expand size={13} /></a></div>
    </div>, document.body)}
  </>;
}
